import type { CSSProperties, ReactNode } from 'react';
import { LogoMark } from '../ui/Logo';
import { Spinner } from '../ui/Spinner';

/**
 * The app's one auth-wait screen, shared by the two places a sign-in can stall.
 *
 * Signing in crosses BOTH of them in a row: /auth/callback waits for Google's
 * round-trip (AuthCallbackPage), then ProtectedRoute waits for clerk-js to
 * hydrate (AuthLoadingScreen). Until 2026-08-31 those looked nothing alike —
 * the first was a gray "Finalizing authentication / You will be redirected as
 * soon as the Google callback completes." card inside AuthShell, whose
 * background is `var(--color-primary)` (brand blue); the second is this logo
 * and spinner on `var(--color-surface)`. One sign-in therefore painted a blue
 * panel, a sentence naming an OAuth callback, and then a white spinner.
 *
 * Emily, 2026-08-31: "instead of the reauthorizing gmail screen we have ...
 * can we just show the loading screen".
 *
 * Kept as a component rather than copied markup so the two waits cannot drift;
 * AuthCallbackPage.loadingScreen.test.tsx pins that both import from here.
 */

/**
 * Full-screen holding surface. Paired with AUTH_LOADING_SHELL_STYLE. `app-ground`
 * paints the app background texture, so the wait matches the app it opens into.
 */
export const AUTH_LOADING_SHELL_CLASS = 'flex min-h-screen items-center justify-center px-4 app-ground';

/**
 * The background is a token read at runtime rather than a Tailwind class
 * because `--color-surface` is defined in styles/index.css, which is the
 * source of truth for it (see the "dashboard CSS must use var(--color-*)"
 * rule in CLAUDE.md).
 */
export const AUTH_LOADING_SHELL_STYLE: CSSProperties = {
  backgroundColor: 'var(--color-surface)',
};

/**
 * Logo mark over a spinner. `children` renders inside the same centered
 * column, for the one thing AuthCallbackPage must keep mounted beside it
 * (Clerk's #clerk-captcha target).
 */
export function AuthLoadingIndicator({ children }: { children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-4">
      <LogoMark className="w-8 h-8" />
      <Spinner size="lg" />
      {children}
    </div>
  );
}
